/**
 * labelFrom — resolve a backend value through a label map, and mark the miss.
 *
 * @description
 *   One helper for a problem three surfaces had already solved three times.
 *   `POInbox.jsx` and `RMAInbox.jsx` each carried their own copy of this
 *   function and of the hint string below, byte-identical; `EinordnungCard`
 *   had none and rendered the raw backend value instead — which the Regel-16
 *   gate measured as B-1 on PR #2413 ("Relevance: rma", "Category: retoure").
 *
 *   The point is the SECOND return value. The older form `MAP[v] || v` made an
 *   unmapped value indistinguishable from a mapped one: the raw snake_case
 *   appeared in a status column as if the map had produced it. Hiding it would
 *   be worse — the operator would see nothing at all — so the raw value stays,
 *   humanised, with `unmapped: true` for the caller to mark.
 *
 *   The rule itself is §5.4(a) of the inbox concept, which both callers cited
 *   by name in their own copies. Only the code moved; the wording below is the
 *   one they already shipped, byte-identical, hint string included.
 *
 * @useWhen
 *   - Rendering any backend enum whose value set is not closed on this side
 *   - Needing to tell "mapped" from "we have never seen this value"
 *
 * @dontUseFor
 *   - A value with a CLOSED vocabulary that the caller must cover completely —
 *     there, a miss is a defect and belongs in a test, not in a tooltip
 *   - Humanising anything other than a snake_case identifier
 *
 * @related
 *   - provenance — the other "meaning, not a renderer" module in this folder
 *   - POInbox / RMAInbox — the two callers that carried copies of this
 *   - EinordnungCard — the caller that had none and rendered raw values
 *
 * @reuseHints
 *   - Any new surface that shows a model-produced `intent` or `kategorie`
 *   - Vendor or fulfilment status columns with a growing value set
 */

//: The wording is load-bearing: it is what tells the operator that the value
//: on screen is a foreign body and not a label this page chose.
export const UNMAPPED_LABEL_HINT = 'Unmapped backend value — not in this page\'s label map'

/**
 * Resolve `value` through `map`.
 *
 * @returns {{label: string, unmapped: boolean}} `label` is the mapped text, the
 *   humanised raw value when the map misses, or an em dash for an empty value.
 *   `unmapped` is true only for the miss — the caller uses it to mark the value
 *   (the house form is `title={UNMAPPED_LABEL_HINT}`).
 */
export function labelFrom(map, value) {
  if (!value) return { label: '—', unmapped: false }
  const hit = map[value]
  if (hit) return { label: hit, unmapped: false }
  return { label: String(value).replace(/_/g, ' '), unmapped: true }
}
